import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import Seo, { breadcrumbLd } from "../components/Seo";

const resolveImg = (url) =>
  url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;

export default function Journal() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/content", { params: { type: "post", limit: 30 } })
      .then(({ data }) => setPosts(data))
      .catch(() => {
        /* silent failure — empty post list is acceptable */
      });
  }, []);

  return (
    <div data-testid="journal-page" className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
      <Seo
        title="African Stories — Essays on Contemporary African Art"
        description="Curated essays, interviews and reportage on contemporary African art — from biennales to studios, written by editors and artists working across the continent and its diaspora."
        path="/journal"
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "African Stories", path: "/journal" },
        ])}
      />
      <p className="label-eyebrow text-[#1E3A8A]">— Editorial</p>
      <h1 className="font-serif-display text-5xl md:text-7xl tracking-tighter leading-none font-medium mt-4 text-[#111]">
        African Stories
      </h1>
      <p className="text-[#555] mt-4 max-w-2xl">
        Curatorial essays, artist features, and dispatches from the AfricaArtMasters editorial team.
      </p>

      <div className="mt-16">
        {posts.length === 0 ? (
          <p className="font-serif-display text-3xl text-[#555]">No issues published yet.</p>
        ) : (
          <div className="space-y-16">
            {posts.map((p, i) => (
              <Link
                to={`/journal/${p.slug}`}
                key={p.id}
                data-testid={`journal-post-${p.id}`}
                className="grid grid-cols-12 gap-y-8 gap-x-4 lg:gap-8 group items-start"
              >
                <div className="col-span-12 md:col-span-5">
                  <div className="aspect-[4/5] bg-[#F2EDE5] overflow-hidden">
                    {p.cover_image ? (
                      <img
                        src={resolveImg(p.cover_image)}
                        alt={`${p.title} — African Stories editorial cover`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#1E3A8A] font-serif-display text-6xl">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-7 md:pt-6">
                  <p className="label-eyebrow text-[#1E3A8A]">N° {String(i + 1).padStart(2, "0")} · {p.created_at?.slice(0, 10)}</p>
                  <h2 className="font-serif-display text-4xl md:text-5xl mt-3 text-[#111] font-medium tracking-tight group-hover:text-[#1E3A8A] transition-colors">
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="mt-5 text-lg text-[#333] leading-relaxed font-light max-w-xl">{p.excerpt}</p>
                  )}
                  <p className="label-eyebrow text-[#1E3A8A] mt-6 inline-flex items-center gap-1">
                    Read article →
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
